"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGqlClient } from "@/Providers/GQLProvider";
import { paymentSourceCookie, PAYMENT_STORAGE_KEY, resolveGatewayCode } from "@/lib/paymentSources";

interface ContextValue {
  /** Gateway code from SSR / the 1-hour cookie, or null on a direct visit. */
  activeGateway: string | null;
  /** Always true after SSR resolve — kept so checkout callers stay stable. */
  sourceReady: boolean;
}

const Ctx = createContext<ContextValue | undefined>(undefined);

export function PaymentSourceProvider({
  initialGateway,
  children,
}: {
  initialGateway: string | null;
  children: React.ReactNode;
}) {
  const gqlClient = useGqlClient();
  const [activeGateway, setActiveGateway] = useState<string | null>(initialGateway);

  const detectors = useQuery({
    queryKey: [gqlClient.getPaymentSourceDetectors.name],
    queryFn: () => gqlClient.getPaymentSourceDetectors(),
    staleTime: 5 * 60_000,
  });

  // shortcut: sync cookie on client — useSearchParams in root layout blanks the whole page until JS
  useEffect(() => {
    if (detectors.isPending) return;
    const list = detectors.data?.getPaymentSourceDetectors ?? [];
    const params = new URLSearchParams(window.location.search);
    const detected =
      resolveGatewayCode(list, params.get("utm_source"), params.get("source"), document.referrer) ??
      activeGateway;

    if (!detected) return;
    if (detected !== activeGateway) setActiveGateway(detected);
    document.cookie = paymentSourceCookie(detected);
  }, [activeGateway, detectors.data, detectors.isPending]);

  const value = useMemo(() => ({ activeGateway, sourceReady: true as const }), [activeGateway]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePaymentSource = () => {
  const context = useContext(Ctx);
  if (!context) throw new Error("usePaymentSource must be used within PaymentSourceProvider");
  return context;
};

export { PAYMENT_STORAGE_KEY };
